import { useQuery } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { Analyze, Device, DeviceStatus, ModuleOut } from "../api/types";
import { ErrorBox, Loading, StatusDot, relativeTime } from "../components/common";
import {
  IconAccessibility,
  IconBell,
  IconCapture,
  IconChevron,
  IconKeyboard,
  IconWarning,
  type IconProps,
} from "../components/icons";
import { useI18n, type Translate } from "../i18n";
import { localized } from "../i18n/localized";

/**
 * The grants a phone reports on every heartbeat, in the order an operator
 * cares about them. `required` marks the ones without which a scenario cannot
 * run at all — those turn red when missing, the rest stay grey.
 *
 * Each carries its glyph, because a collapsed row shows the four as icons and
 * the open one writes them out: one list, so the drawing and the sentence
 * cannot come to describe different grants.
 */
const PERMISSIONS: Array<{
  key: string;
  required: boolean;
  icon: (props: IconProps) => JSX.Element;
}> = [
  { key: "accessibility", required: true, icon: IconAccessibility },
  { key: "screen_capture", required: true, icon: IconCapture },
  { key: "ime", required: true, icon: IconKeyboard },
  { key: "notifications", required: false, icon: IconBell },
];

/** Among phones enrolled at the same time, trouble still floats to the top. */
const STATUS_ORDER = ["needs_attention", "quarantined", "offline", "busy", "online"];

/** The status chips, in the order an operator scans them. */
const STATUS_FILTERS: DeviceStatus[] = [
  "online",
  "busy",
  "needs_attention",
  "offline",
  "quarantined",
];

/** A phone enrolled this recently still gets a "new" tag by its name. */
const NEW_FOR_MS = 24 * 60 * 60 * 1000;

/** What a grant is: granted, missing and needed, or missing and merely
 *  advisable. The three are a colour in the row and a word in the panel. */
function grantTone(granted: boolean, required: boolean): "on" | "missing" | "off" {
  return granted ? "on" : required ? "missing" : "off";
}

function grantTitle(
  permission: (typeof PERMISSIONS)[number],
  granted: boolean,
  t: Translate,
): string {
  const name = t(`perm.${permission.key}`);
  if (granted) return `${name} — ${t("perm.granted")}`;
  return `${name} — ${t("perm.notGranted")}. ${t(`perm.${permission.key}.why`)}`;
}

/**
 * The four grants as four glyphs, which is the whole of the collapsed row's
 * permissions column (§ the row that used to read "✗ Keyboard ✗ Notifications"
 * and wrapped).
 *
 * Every reported grant is drawn, granted ones included: a row of four quiet
 * ticks and one red keyboard is read at a glance and in the same place on
 * every row, where a list of only what is missing moves under the eye. The
 * words are on hover, and written out in the panel below.
 */
function PermissionIcons({ device }: { device: Device }) {
  const { t } = useI18n();
  const reported = device.permissions ?? {};
  const listed = PERMISSIONS.filter((permission) => permission.key in reported);

  if (listed.length === 0) {
    return (
      <span className="muted" title={t("devices.notReportedYet")}>
        —
      </span>
    );
  }

  return (
    <div className="perm-icons">
      {listed.map((permission) => {
        const granted = reported[permission.key];
        const Glyph = permission.icon;
        return (
          <span
            key={permission.key}
            className={`perm-icon ${grantTone(granted, permission.required)}`}
            title={grantTitle(permission, granted, t)}
          >
            <Glyph size={17} />
            <span className="sr-only">{grantTitle(permission, granted, t)}</span>
          </span>
        );
      })}
    </div>
  );
}

/** The same four, in words, for the open row. */
function PermissionList({ device }: { device: Device }) {
  const { t } = useI18n();
  const reported = device.permissions ?? {};
  const listed = PERMISSIONS.filter((permission) => permission.key in reported);
  if (listed.length === 0) {
    return <p className="muted">{t("devices.notReported")}</p>;
  }
  return (
    <ul className="perm-list">
      {listed.map((permission) => {
        const granted = reported[permission.key];
        return (
          <li key={permission.key} className={grantTone(granted, permission.required)}>
            <i aria-hidden="true">{granted ? "✓" : "✗"}</i>
            <span>{t(`perm.${permission.key}`)}</span>
            {!granted && <em>{t(`perm.${permission.key}.why`)}</em>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One pip + name per enabled analyze, straight from the server's judgement.
 * A filled pip is a match, a hollow one is not; the verdict word is kept for
 * screen readers and the tooltip, where it does not crowd the name.
 */
function AnalyzeChecks({ device, analyzes }: { device: Device; analyzes: Analyze[] }) {
  const { t } = useI18n();
  const enabled = analyzes.filter((analyze) => analyze.enabled);
  if (enabled.length === 0) {
    return (
      <span className="muted" title={t("devices.manageAnalyzes")}>
        {t("devices.noneDefined")}
      </span>
    );
  }

  return (
    <ul className="analyze-hits">
      {enabled.map((analyze) => {
        const on = device.analyze_matches[analyze.id] === true;
        const verdict = on ? t("analyzes.matchYes") : t("analyzes.matchNo");
        const summary = analyze.criteria.length
          ? analyze.criteria.map((c) => `${c.kind} ${c.value}`).join(" + ")
          : t("devices.noCriteria");
        return (
          <li key={analyze.id} className={on ? "on" : "off"} title={`${verdict}: ${summary}`}>
            <i className="pip" aria-hidden="true" />
            <span className="sr-only">{verdict} </span>
            <span className="name">{analyze.name.trim() || t("devices.unnamed")}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The same verdicts as pips alone, for the collapsed row: how many sets this
 * phone matches, in the width of a thumbnail. Which set each pip is stays on
 * hover, and the named list is one click away in the panel below.
 */
function AnalyzePips({ device, analyzes }: { device: Device; analyzes: Analyze[] }) {
  const { t } = useI18n();
  const enabled = analyzes.filter((analyze) => analyze.enabled);
  if (enabled.length === 0) {
    return (
      <span className="muted" title={t("devices.noneDefined")}>
        —
      </span>
    );
  }
  return (
    <ul className="analyze-pips">
      {enabled.map((analyze) => {
        const on = device.analyze_matches[analyze.id] === true;
        const name = analyze.name.trim() || t("devices.unnamed");
        const verdict = on ? t("analyzes.matchYes") : t("analyzes.matchNo");
        return (
          <li key={analyze.id} className={on ? "on" : "off"} title={`${name} — ${verdict}`}>
            <i className="pip" aria-hidden="true" />
            <span className="sr-only">{`${name}: ${verdict}`}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One chip per module this phone's APK carries, tinted by what the module
 * said on the last heartbeat (MODULES §4.4). A phone with none — every phone,
 * until a customer switches one on — shows nothing at all.
 */
function ModuleChips({ device, catalogue }: { device: Device; catalogue: ModuleOut[] }) {
  const { t, lang } = useI18n();
  const beats = Object.entries(device.modules ?? {});
  if (beats.length === 0) return null;
  return (
    <div className="module-chips">
      {beats.map(([id, beat]) => (
        <span
          key={id}
          className={`module-chip ${beat.state}`}
          title={beat.message ?? t(`panel.moduleState.${beat.state}`)}
        >
          {localized(
            catalogue.find((module) => module.id === id)?.name,
            lang,
            id,
          )}
        </span>
      ))}
    </div>
  );
}

function countryLabel(iso: string, lang: string): string {
  try {
    return new Intl.DisplayNames([lang], { type: "region" }).of(iso) ?? iso;
  } catch {
    return iso;
  }
}

/**
 * Country as its ISO code — the same code an analyze set is written against,
 * and it never wraps the way "United States of America" does — with the full
 * name and the phone's languages on hover.
 *
 * The languages themselves ("en-US, ru-RU, de-DE") are a panel's job: three of
 * them are wider than the column and say less, in a row, than the two letters
 * beside them.
 */
function Locale({ device, lang }: { device: Device; lang: string }) {
  const country = device.country?.toUpperCase();
  const languages = device.languages?.length ? device.languages.join(", ") : "";
  if (!country && !languages) return <span className="muted">—</span>;
  const title = [country && countryLabel(country, lang), languages].filter(Boolean).join(" · ");
  return (
    <div className="locale" title={title}>
      {country ? <span className="country">{country}</span> : <span className="muted">—</span>}
    </div>
  );
}

/** One line of the detail panel. Nothing at all when the phone has not
 *  reported the field: an empty row of dashes is noise, not information. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** One reading in the panel's top strip: a small label over its value, so a
 *  row of them reads left to right like a row of dials. Same rule as a Fact:
 *  nothing at all when the phone has not said. */
function Vital({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null;
  return (
    <div className="vital">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function whenExactly(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : new Date(ms).toLocaleString();
}

/**
 * What an open row shows (the other half of "the collapsed row is glyphs").
 *
 * Two tiers. The strip on top is what is going on: the state, both timestamps
 * in full (because "22h ago" is the right answer in a column and the wrong one
 * when you are working out what happened this morning), what is wrong with the
 * phone, and the way into its panel. Under it, three columns of what the phone
 * is: the hardware, the grants in words, the sets and modules by name — and
 * across the bottom, the note somebody left on it.
 */
function DeviceDetail({
  device,
  analyzes,
  catalogue,
  lang,
}: {
  device: Device;
  analyzes: Analyze[];
  catalogue: ModuleOut[];
  lang: string;
}) {
  const { t } = useI18n();
  const country = device.country?.toUpperCase();
  const screen =
    device.screen_width && device.screen_height
      ? `${device.screen_width} × ${device.screen_height}`
      : null;
  // The agent reports Int.MIN_VALUE when it cannot read the battery; that is
  // "unknown", not a number to print.
  const battery =
    device.battery_level !== null && device.battery_level >= 0 && device.battery_level <= 100
      ? `${device.battery_level}%`
      : null;
  return (
    <div className="device-detail">
      <header className="detail-head">
        <dl className="detail-vitals">
          <Vital label={t("devices.detail.state")} value={<StatusDot value={device.status} />} />
          <Vital label={t("devices.lastHeartbeat")} value={whenExactly(device.last_heartbeat_at)} />
          <Vital label={t("devices.detail.enrolled")} value={whenExactly(device.created_at)} />
          <Vital label={t("panel.foreground")} value={device.foreground_app} />
        </dl>
        {device.needs_attention_reason && (
          <p className="detail-alert">
            <IconWarning size={15} />
            <span className="error-text">{device.needs_attention_reason}</span>
          </p>
        )}
        <Link className="detail-link" to={`/devices/${device.id}`}>
          {t("devices.openPanel")}
          <IconChevron size={14} />
        </Link>
      </header>

      <div className="detail-body">
        <section>
          <h4>{t("devices.detail.phone")}</h4>
          <dl>
            <Fact label={t("devices.detail.model")} value={device.model} />
            <Fact
              label={t("devices.detail.os")}
              value={device.os_version ? `${device.platform} ${device.os_version}` : null}
            />
            <Fact label={t("devices.detail.screen")} value={screen} />
            <Fact label={t("devices.detail.battery")} value={battery} />
            <Fact label={t("devices.detail.agent")} value={device.agent_version} />
            <Fact label={t("devices.detail.sim")} value={device.sim_state} />
            <Fact
              label={t("devices.detail.locale")}
              value={
                [country && countryLabel(country, lang), device.languages?.join(", ")]
                  .filter(Boolean)
                  .join(" · ") || null
              }
            />
            <Fact label={t("devices.detail.tags")} value={device.tags.join(", ")} />
          </dl>
        </section>

        <section>
          <h4>{t("devices.th.permissions")}</h4>
          <PermissionList device={device} />
        </section>

        <div className="stack">
          <section>
            <h4>{t("devices.th.analyzes")}</h4>
            <AnalyzeChecks device={device} analyzes={analyzes} />
          </section>
          {Object.keys(device.modules ?? {}).length > 0 && (
            <section>
              <h4>{t("nav.modules")}</h4>
              <ModuleChips device={device} catalogue={catalogue} />
            </section>
          )}
        </div>

        {device.note && (
          <section className="wide">
            <h4>{t("panel.noteHeading")}</h4>
            <p className="muted">{device.note}</p>
          </section>
        )}
      </div>
    </div>
  );
}

function enrolledMs(device: Device): number {
  const ms = Date.parse(device.created_at);
  return Number.isNaN(ms) ? 0 : ms;
}

function isNew(device: Device): boolean {
  const ms = enrolledMs(device);
  return ms > 0 && Date.now() - ms < NEW_FOR_MS;
}

function addedTitle(device: Device, t: Translate): string | undefined {
  const ms = enrolledMs(device);
  return ms > 0 ? t("devices.enrolled", { at: new Date(ms).toLocaleString() }) : undefined;
}

/** Everything the search box looks at, lowercased once per render. */
function haystack(device: Device): string {
  return [
    device.name,
    device.model,
    device.os_version,
    device.agent_version,
    device.country,
    device.note,
    ...device.tags,
    ...device.languages,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export default function Devices() {
  const { t, lang } = useI18n();
  const devices = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.devices(),
    refetchInterval: 5000,
  });
  const analyzes = useQuery({ queryKey: ["analyzes"], queryFn: api.analyzes });
  // Only for the chips' labels: the fleet list is fine without it, so a
  // release with no modules costs one cached request and nothing else.
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | "all">("all");
  // A set rather than one id: two phones being compared are two rows open at
  // once, and closing the first to read the second is the thing that makes an
  // accordion tiring.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  if (devices.isLoading) return <Loading what={t("loading.devices")} />;
  if (devices.error) return <ErrorBox error={devices.error} />;

  const fleet = devices.data ?? [];
  const counts = new Map<DeviceStatus, number>(STATUS_FILTERS.map((status) => [status, 0]));
  for (const device of fleet) counts.set(device.status, (counts.get(device.status) ?? 0) + 1);

  const needle = search.trim().toLowerCase();
  const visible = fleet
    .filter((device) => statusFilter === "all" || device.status === statusFilter)
    .filter((device) => !needle || haystack(device).includes(needle))
    .sort(
      (a, b) =>
        enrolledMs(b) - enrolledMs(a) ||
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        a.name.localeCompare(b.name),
    );
  const filtered = visible.length !== fleet.length;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  return (
    <>
      <div className="page-header">
        <h2>{t("nav.devices")}</h2>
      </div>

      <div className="devices-toolbar">
        <div className="search-box">
          <input
            type="search"
            placeholder={t("devices.searchPlaceholder")}
            aria-label={t("devices.searchAria")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search && (
            <button
              type="button"
              className="search-clear"
              aria-label={t("devices.clearSearch")}
              title={t("devices.clearSearch")}
              onClick={() => setSearch("")}
            >
              ✕
            </button>
          )}
        </div>
        <div className="toolgroup seg" role="radiogroup" aria-label={t("devices.filterByStatus")}>
          <button
            type="button"
            role="radio"
            aria-checked={statusFilter === "all"}
            className={statusFilter === "all" ? "on" : ""}
            onClick={() => setStatusFilter("all")}
          >
            {t("devices.all")} <span className="count">{fleet.length}</span>
          </button>
          {STATUS_FILTERS.map((status) => {
            const count = counts.get(status) ?? 0;
            // A state no phone is in fades, so the eye lands on the ones that matter.
            const classes = [statusFilter === status && "on", count === 0 && "zero"];
            return (
              <button
                key={status}
                type="button"
                role="radio"
                aria-checked={statusFilter === status}
                className={classes.filter(Boolean).join(" ")}
                onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}
              >
                {t(`status.${status}`)} <span className="count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card table-wrap" style={{ padding: 0 }}>
        <table className="device-table">
          <thead>
            <tr>
              <th>{t("devices.th.device")}</th>
              <th>{t("devices.th.status")}</th>
              <th>{t("devices.th.permissions")}</th>
              <th>
                <Link to="/analyzes" title={t("devices.manageAnalyzes")}>
                  {t("devices.th.analyzes")}
                </Link>
              </th>
              <th>{t("devices.th.locale")}</th>
              <th>{t("devices.th.added")}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((device) => {
              const expanded = open.has(device.id);
              return (
                <Fragment key={device.id}>
                  {/* The whole row is the control — a fleet is read by
                      pointing at a phone, not by finding its chevron — and the
                      two things inside it that are not "expand me" (the name's
                      link, the chevron itself) say so. */}
                  <tr
                    className={expanded ? "device-row open" : "device-row"}
                    onClick={() => toggle(device.id)}
                  >
                    <td>
                      <div className="device-name">
                        <button
                          type="button"
                          className="row-toggle"
                          aria-expanded={expanded}
                          aria-controls={`device-detail-${device.id}`}
                          aria-label={t(expanded ? "devices.collapse" : "devices.expand", {
                            name: device.name,
                          })}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle(device.id);
                          }}
                        >
                          <IconChevron size={15} />
                        </button>
                        <Link
                          to={`/devices/${device.id}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {device.name}
                        </Link>
                        {isNew(device) && (
                          <span className="new-dot" title={addedTitle(device, t)}>
                            <span className="sr-only">{t("devices.new")}</span>
                          </span>
                        )}
                        {device.needs_attention_reason && (
                          <span className="attention-icon" title={device.needs_attention_reason}>
                            <IconWarning size={15} />
                            <span className="sr-only">{device.needs_attention_reason}</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="device-status">
                        <StatusDot value={device.status} compact />
                        <span className="muted" title={t("devices.lastHeartbeat")}>
                          {relativeTime(device.last_heartbeat_at, t)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <PermissionIcons device={device} />
                    </td>
                    <td className="analyzes">
                      <AnalyzePips device={device} analyzes={analyzes.data ?? []} />
                    </td>
                    <td>
                      <Locale device={device} lang={lang} />
                    </td>
                    <td className="muted nowrap" title={addedTitle(device, t)}>
                      {relativeTime(device.created_at, t)}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="device-detail-row" id={`device-detail-${device.id}`}>
                      <td colSpan={6}>
                        <DeviceDetail
                          device={device}
                          analyzes={analyzes.data ?? []}
                          catalogue={modules.data ?? []}
                          lang={lang}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {visible.length === 0 && fleet.length > 0 && (
              <tr className="empty-row">
                <td colSpan={6}>
                  <p className="muted" style={{ margin: 0 }}>
                    {t("devices.noMatch", {
                      what: needle ? `“${search.trim()}”` : t("devices.thisFilter"),
                    })}
                  </p>
                  <button style={{ marginTop: 10 }} onClick={clearFilters}>
                    {t("devices.clearFilters")}
                  </button>
                </td>
              </tr>
            )}
            {fleet.length === 0 && (
              <tr className="empty-row">
                <td colSpan={6} className="muted">
                  {t("devices.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {fleet.length > 0 && (
        <p className="muted devices-count" style={{ fontSize: 12, margin: "10px 2px" }}>
          {t("devices.showing", { shown: visible.length, total: fleet.length })}
          {filtered && ` — ${t("devices.filtersOn")}`}.
        </p>
      )}
    </>
  );
}
